import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';

import { UserLocationRole } from '@shared/user/enums';
import { TrTableLayoutModeService } from '../../services/tr-table-layout-mode.service';
import { TrTableRegistryService } from '../../services/tr-table-registry.service';

/** One row of the gear's popover: an action, or a divider between sections. */
interface TableSettingsMenuItem {
  /** Stable handle for `data-testid` (labels change with state). */
  id?: string;
  label?: string;
  icon?: string;
  disabled?: boolean;
  separator?: boolean;
  command?: () => void;
}

/**
 * Table settings button — the gear that opens a table's layout options.
 *
 * Lives in the page toolbar (always the left-most control above the table) and
 * targets the table with the matching `stateId` through
 * {@link TrTableRegistryService}, so it works from outside the `*ngIf` that
 * renders the table. The popover is the app's toolbar menu (`.tr-popover-menu`,
 * shared with the customers Filter); it owns the table-wide items (Edit Table Layout,
 * Show/Hide Columns and the location defaults), while the column menu keeps only
 * per-column actions — and is itself only reachable while layout editing is on
 * ({@link TrTableLayoutModeService}).
 *
 * When the page has no table right now (an empty list), the menu still opens
 * with its items disabled rather than silently doing nothing.
 */
@Component({
  selector: 'tablesready-table-settings',
  templateUrl: './table-settings.component.html',
  styleUrls: ['./table-settings.component.scss'],
  standalone: false,
})
export class TableSettingsComponent implements OnInit, OnDestroy {
  /** `stateId` of the table this button configures (e.g. `'waitlist'`). */
  @Input() stateId?: string;
  /** Tooltip / aria label for the button. */
  @Input() label = 'Table settings';
  @Input() userLocationRole = UserLocationRole.user;

  menuItems: TableSettingsMenuItem[] = [];

  /**
   * Show the unsaved-layout dot: there are column changes a location default
   * wouldn't have, *and* the user is in the mode where they can do something
   * about them. Outside that mode the reminder would be noise on every page.
   */
  unsaved = false;

  private subs?: Subscription;

  constructor(
    private readonly registry: TrTableRegistryService,
    private readonly layoutMode: TrTableLayoutModeService,
  ) {}

  ngOnInit(): void {
    this.subs = combineLatest([this.layoutMode.editing$, this.layoutMode.dirty$]).subscribe(
      ([editing, dirty]) => {
        this.unsaved = editing && dirty;
      },
    );
  }

  ngOnDestroy(): void {
    this.subs?.unsubscribe();
  }

  /**
   * Build the menu against the table that exists *now* — resolving as the popover
   * opens (rather than binding to the registry) keeps the toolbar free of state
   * that changes as tables come and go mid-change-detection.
   */
  buildMenu(): void {
    const table = this.registry.get(this.stateId);
    const disabled = !table;
    const editing = this.layoutMode.editing;
    const canSaveDefault = this.userLocationRole === UserLocationRole.manager;

    // Two sections: what you do to this table's layout now, then the location
    // defaults it can be measured against.
    const items: TableSettingsMenuItem[] = [
      {
        id: 'edit-layout',
        // Reads as the state it will put you in, so one item serves as the
        // toggle: "Edit…" while the header is clean, "Done…" while it isn't.
        label: editing ? 'Done Editing Layout' : 'Edit Table Layout',
        icon: editing ? 'bi bi-check2' : 'bi bi-sliders',
        disabled,
        command: (): void => {
          this.layoutMode.toggle();
        },
      },
      {
        id: 'show-hide-columns',
        label: 'Show/Hide Columns…',
        icon: 'bi bi-layout-three-columns',
        disabled,
        command: (): void => {
          table?.openColumnsModal();
        },
      },
      { separator: true },
      {
        id: 'reset-default',
        label: 'Reset to Location Default',
        icon: 'bi bi-arrow-counterclockwise',
        disabled,
        command: (): void => {
          table?.resetToLocationDefault();
        },
      },
    ];
    if (canSaveDefault) {
      items.push({
        id: 'save-default',
        label: 'Save as Location Default',
        icon: 'bi bi-save',
        disabled,
        command: (): void => {
          table?.saveAsLocationDefault();
        },
      });
    }

    this.menuItems = items;
  }
}
